import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertTriangle, Link2, RefreshCw, Trash2, X } from 'lucide-react'
import type { CaseEndpoint } from '../../../types/case-container'
import type { LinkedItem, LinkedType } from './use-delete-confirmation-modal'

const ENDPOINT_PREVIEW_LIMIT = 10

interface ModalHeaderProps {
  count: number
  deleting: boolean
  onClose: () => void
  t: TFunction<'cases'>
}

export function ModalHeader({ count, deleting, onClose, t }: ModalHeaderProps): ReactElement {
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-red-100 p-2 dark:bg-red-950/50">
          <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('infrastructure.deleteEndpointTitle', { count })}
        </h3>
      </div>
      <button
        onClick={onClose}
        disabled={deleting}
        className="text-slate-400 hover:text-slate-600 disabled:opacity-50 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface WarningStepProps {
  endpoints: CaseEndpoint[]
  count: number
  onClose: () => void
  onProceed: () => void
  t: TFunction<'cases'>
}

export function WarningStep({
  endpoints,
  count,
  onClose,
  onProceed,
  t,
}: WarningStepProps): ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-400">
              {t('infrastructure.permanentAction')}
            </p>
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {t('infrastructure.deleteEndpointWarning', { count })}
            </p>
          </div>
        </div>
      </div>
      <EndpointPreviewList endpoints={endpoints} t={t} />
      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {t('actions.cancel')}
        </button>
        <button
          onClick={onProceed}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('infrastructure.understandContinue')}
        </button>
      </div>
    </div>
  )
}

function EndpointPreviewList({
  endpoints,
  t,
}: {
  endpoints: CaseEndpoint[]
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {t('infrastructure.endpointsToBeDeleted')}
        </p>
      </div>
      <div className="max-h-48 overflow-y-auto">
        <ul className="divide-y divide-slate-200 dark:divide-slate-700">
          {endpoints.slice(0, ENDPOINT_PREVIEW_LIMIT).map((ep) => (
            <li key={ep.id} className="px-3 py-2 text-sm">
              <span className="font-medium text-slate-900 dark:text-slate-100">{ep.hostname}</span>
              <span className="ml-2 text-slate-500 dark:text-slate-400">({ep.deviceType})</span>
            </li>
          ))}
          {endpoints.length > ENDPOINT_PREVIEW_LIMIT && (
            <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
              {t('infrastructure.andMore', { count: endpoints.length - ENDPOINT_PREVIEW_LIMIT })}
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

export function CheckingStep({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-8">
      <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      <p className="text-slate-600 dark:text-slate-400">
        {t('infrastructure.checkingLinkedArtefacts')}
      </p>
    </div>
  )
}

interface BlockedStepProps {
  linkedItems: LinkedItem[]
  onClose: () => void
  t: TFunction<'cases'>
}

export function BlockedStep({ linkedItems, onClose, t }: BlockedStepProps): ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <Link2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-medium text-amber-700 dark:text-amber-400">
              {t('infrastructure.cannotDeleteLinked')}
            </p>
            <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
              {t('infrastructure.linkedArtefactsMessage', { count: linkedItems.length })}
            </p>
          </div>
        </div>
      </div>
      <LinkedItemsList linkedItems={linkedItems} t={t} />
      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onClose}
          className="rounded-lg bg-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          {t('actions.close')}
        </button>
      </div>
    </div>
  )
}

function linkedTypeLabel(linkedType: LinkedType, t: TFunction<'cases'>): string {
  if (linkedType === 'both') return t('infrastructure.linkedToBoth')
  if (linkedType === 'endpoint') return t('infrastructure.linkedToEndpoint')
  return t('infrastructure.linkedToCollection')
}

function LinkedItemsList({
  linkedItems,
  t,
}: {
  linkedItems: LinkedItem[]
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-amber-200 dark:border-amber-800">
      <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/30">
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
          {t('infrastructure.endpointsWithLinkedItems')}
        </p>
      </div>
      <div className="max-h-48 overflow-y-auto">
        <ul className="divide-y divide-amber-200 dark:divide-amber-800">
          {linkedItems.map(({ endpoint, linkedType }) => (
            <li key={endpoint.id} className="px-3 py-2 text-sm">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {endpoint.hostname}
              </span>
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                {linkedTypeLabel(linkedType, t)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

interface ConfirmStepProps {
  count: number
  deleting: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  t: TFunction<'cases'>
}

export function ConfirmStep({
  count,
  deleting,
  onClose,
  onConfirm,
  t,
}: ConfirmStepProps): ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30">
        <p className="text-sm text-green-700 dark:text-green-400">
          {t('infrastructure.noLinkedArtefacts')}
        </p>
      </div>
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
        <p className="text-sm font-medium text-red-700 dark:text-red-400">
          {t('infrastructure.finalConfirmDelete', { count })}
        </p>
      </div>
      <ConfirmActions deleting={deleting} onClose={onClose} onConfirm={onConfirm} t={t} />
    </div>
  )
}

interface ConfirmActionsProps {
  deleting: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  t: TFunction<'cases'>
}

function ConfirmActions({ deleting, onClose, onConfirm, t }: ConfirmActionsProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button
        onClick={onClose}
        disabled={deleting}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        onClick={() => {
          void onConfirm()
        }}
        disabled={deleting}
        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
      >
        {deleting ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t('infrastructure.deleting')}
          </>
        ) : (
          <>
            <Trash2 className="h-4 w-4" />
            {t('infrastructure.deletePermanently')}
          </>
        )}
      </button>
    </div>
  )
}
